import { Bookmark, Delete, Edit, Label } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import {
	Contact,
	Department,
	TimeLabel,
	TimeLabelGroup,
	useGetAllTimeLabelGroupsOfDepartmentLazyQuery,
	useGetAllTimeLabelsOfDepartmentLazyQuery,
} from "@repo/server-api";
import { useTranslation } from "next-i18next";
import { useMemo, useState } from "react";

import { useAllDepartmentsDataSource } from "../datasources/AllDepartmentsDataSource";
import ConformationDialog from "../dialogs/Conformation";
import DialogDepartment from "../dialogs/DialogDepartment";
import DialogTimeLabelGroupsOfDepartment from "../dialogs/DialogTimeLabelGroupsOfDepartment";
import DialogTimeLabelsOfDepartment from "../dialogs/DialogTimeLabelsOfDepartment";
import DetailPanelContentDepartments from "../lists/DetailsPanelContentDepartments";
import List from "../lists/List";

export default function Departments(
	props: Readonly<{
		contact: Contact | null;
	}>,
) {
	const { t } = useTranslation();
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [department, setDepartment] = useState<Department | null>(null);

	const allDepartmentsDataSource = useAllDepartmentsDataSource();

	const [getTimeLabelGroups] = useGetAllTimeLabelGroupsOfDepartmentLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [getTimeLabels] = useGetAllTimeLabelsOfDepartmentLazyQuery({
		fetchPolicy: "no-cache",
	});

	const columns: GridColDef[] = useMemo(
		() => [
			{
				field: "name",
				headerName: t("name"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					return params.row.name;
				},
			},
			{
				field: "description",
				headerName: t("no-description"),
				width: 250,
				renderCell: (params: GridCellParams) => {
					return params.row.description;
				},
			},
			{
				field: "leader",
				headerName: t("contractors.department.table.leader"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					return params.row.leader.displayName;
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 300,
				renderCell: (params: GridCellParams) => {
					const [openConformationDialog, setOpenConformationDialog] = useState(false);
					const [openDialogTimeLabels, setOpenDialogTimeLabels] = useState(false);
					const [openDialogTimeLabelGroups, setOpenDialogTimeLabelGroups] = useState(false);

					const [timeLabels, setTimeLabels] = useState<TimeLabel[]>([]);

					const [timeLabelGroups, setTimeLabelGroups] = useState<TimeLabelGroup[]>([]);

					//straja

					const openEdit = (d: Department | null) => {
						setDepartment(d);
						setOpenDialog(true);
					};

					const openTimeLabels = (d: Department) => {
						setDepartment(d);
						if (d.id) {
							getTimeLabels({
								variables: {
									departmentId: d.id,
									first: 100,
									showDeleted: false,
									onlyActive: false,
								},
							}).then((res) => {
								if (res.data && res.data.allTimeLabelsOfDepartment) {
									const tmp: TimeLabel[] =
										res.data.allTimeLabelsOfDepartment?.nodes?.map((label) => ({
											...(label as unknown as TimeLabel),
										})) ?? [];
									setTimeLabels(tmp);
								}
							});
						}
						setOpenDialogTimeLabels(true);
					};

					const openTimeLabelGroups = (d: Department) => {
						setDepartment(d);
						if (d.id) {
							getTimeLabelGroups({
								variables: {
									departmentId: d.id,
									first: 100,
								},
							}).then((res) => {
								if (res.data && res.data.allTimeLabelGroupsOfDepartment) {
									const tmp: TimeLabelGroup[] =
										res.data.allTimeLabelGroupsOfDepartment?.nodes?.map((group) => ({
											...(group as unknown as TimeLabelGroup),
										})) ?? [];
									setTimeLabelGroups(tmp);
								}
							});
						}
						setOpenDialogTimeLabelGroups(true);
					};

					const refreshData = () => {
						const pageModel = {
							page: 0,
							pageSize: 10,
						};
						const searchBy = "";
						allDepartmentsDataSource.fetchDataForContact?.(
							pageModel,
							searchBy,
							props.contact?.id ?? "",
						);
					};

					return (
						<div>
							<Button
								onClick={() => {
									openTimeLabels(params.row);
								}}>
								<Label />
							</Button>
							<Button
								onClick={() => {
									openTimeLabelGroups(params.row);
								}}>
								<Bookmark />
							</Button>
							<Button data-testid="edit-button" onClick={() => setOpenConformationDialog(true)}>
								<Delete />
							</Button>
							<Button data-testid="edit-button" onClick={() => openEdit(params.row)}>
								<Edit />
							</Button>
							<ConformationDialog
								openPopup={openConformationDialog}
								setOpenDeleteConformation={setOpenConformationDialog}
								text={t("contractors.deleteDepartmentMessage")}
								handleYes={() => {
									if (allDepartmentsDataSource.deleteData)
										allDepartmentsDataSource.deleteData(params.row.id);
									setOpenConformationDialog(false);
								}}
							/>
							<DialogTimeLabelsOfDepartment
								open={openDialogTimeLabels}
								setOpen={setOpenDialogTimeLabels}
								department={
									params.row ?? {
										id: "",
										name: "",
										description: "",
										leader: { displayName: "" },
									}
								}
								timeLabels={timeLabels ?? []}
								refreshData={refreshData}
							/>
							<DialogTimeLabelGroupsOfDepartment
								open={openDialogTimeLabelGroups}
								setOpen={setOpenDialogTimeLabelGroups}
								department={
									params.row ?? {
										id: "",
										name: "",
										description: "",
										leader: { displayName: "" },
									}
								}
								timeLabelGroups={timeLabelGroups ?? []}
								refreshData={refreshData}
							/>
						</div>
					);
				},
			},
		],

		[t],
	);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<List
				dataSource={allDepartmentsDataSource}
				columns={columns as GridCellParams<Department>[]}
				contactId={props.contact?.id}
				hasDetailPanelContent={true}
				detailPanelContent={(row?: Department) => (
					<DetailPanelContentDepartments row={row ?? null} contactId={props.contact?.id ?? ""} />
				)}>
				<Button
					data-testid="add-button"
					variant="contained"
					onClick={() => {
						setDepartment(null);
						setOpenDialog(true);
					}}
					className="blueButton">
					{t("add-new-department")}
				</Button>
			</List>
			<DialogDepartment
				refreshData={allDepartmentsDataSource.refresh ? allDepartmentsDataSource.refresh : () => {}}
				departmant={department}
				open={openDialog}
				setOpen={setOpenDialog}
				contactId={props.contact?.id ?? ""}
			/>
		</Box>
	);
}
